/**
 * Statsig 事件总表。最多两层：模块.事件。
 *
 *   import { STATSIG, PAGE } from 'statsig/events'
 *   STATSIG.AUTH.LOGIN
 *   STATSIG.COMMON.VIEW
 *   STATSIG.LOAN.TAB
 *   STATSIG.COMMON.FEEDBACK
 *
 * Management / Settings / Growth Hub / GMCC 只是文件目录，不进调用路径。
 * 上报名仍是 模块_事件，例如 COMPANY_PROFILE_UPDATE。
 * 页面访问统一 STATSIG.COMMON.VIEW → PAGE_VIEW，value 用 PAGE。
 * Management 加 mgmt_ 前缀，Settings 加 settings_ 前缀。
 */
import { AuthEvent } from './auth'
import { DashboardEvent } from './dashboard'
import { LoansEvent } from './loans'
import { LoanEvent } from './loan'
import { BorrowersEvent } from './borrowers'
import { BorrowerEvent } from './borrower'
import { RatesEvent } from './rates'
import { GmccProgramMapsEvent } from './gmcc/programMaps'
import { GmccFlyersHubEvent } from './gmcc/flyersHub'
import { GmccCelebrityEvent } from './gmcc/celebrity'
import { GmccUniverseEvent } from './gmcc/universe'
import { GmccDiamondEvent } from './gmcc/diamond'
import { MgmtAppSettingEvent } from './management/applicationSetting'
import { MgmtLendersEvent } from './management/lenders'
import { MgmtCallReportsEvent } from './management/callReports'
import { MgmtCompanyProfileEvent } from './management/companyProfile'
import { MgmtPublicSiteIntegrationEvent } from './management/publicSiteIntegration'
import { MgmtCommunicationSetupEvent } from './management/communicationSetup'
import { MgmtTeamCreditReportEvent } from './management/teamCreditReport'
import { MgmtUserManagementEvent } from './management/userManagement'
import { MgmtClosingCostSettingEvent } from './management/closingCostSetting'
import { MgmtBillingManagementEvent } from './management/billingManagement'
import { SettingsProfileEvent } from './settings/profile'
import { SettingsFeatureCustomizationEvent } from './settings/featureCustomization'
import { SettingsDocumentTemplatesEvent } from './settings/documentTemplates'
import { SettingsStrataAIEvent } from './settings/strata'
import { SettingsPersonalCreditReportEvent } from './settings/personalCreditReport'
import { SettingsOnePageAppSetupEvent } from './settings/onePageAppSetup'
import { SettingsOpenHouseSignInEvent } from './settings/openHouseSignIn'
import { GrowthHubOverviewEvent } from './growthHub/overview'
import { GrowthHubPersonalSiteEvent } from './growthHub/personalSite'
import { GrowthHubOpenHouseFinderEvent } from './growthHub/openHouseFinder'
import { PaymentEvent } from './payment'
import { CommonEvent } from './common'

export * from './auth'
export * from './dashboard'
export * from './loans'
export * from './loan'
export * from './borrowers'
export * from './borrower'
export * from './rates'
export * from './gmcc'
export * from './management'
export * from './settings'
export * from './growthHub'
export * from './payment'
export * from './common'

export const STATSIG = {
    /** 登录 / 注册 */
    AUTH: AuthEvent,
    /** Dashboard（/app/welcome） */
    DASHBOARD: DashboardEvent,
    /** Loans（/app/active-loans） */
    LOANS: LoansEvent,
    /** Loan（/app/borrower/:id） */
    LOAN: LoanEvent,
    /** Borrowers 列表（/app/borrowers） */
    BORROWERS: BorrowersEvent,
    /** Borrower 档案（/app/borrowers 同页详情） */
    BORROWER: BorrowerEvent,
    /** Rates（/app/ppe-pricing） */
    RATES: RatesEvent,
    /** GMCC Specials → Program maps */
    PROGRAM_MAPS: GmccProgramMapsEvent,
    /** GMCC Specials → Flyers hub */
    FLYERS_HUB: GmccFlyersHubEvent,
    /** GMCC Specials → Celebrity */
    CELEBRITY: GmccCelebrityEvent,
    /** GMCC Specials → Universe */
    UNIVERSE: GmccUniverseEvent,
    /** GMCC Specials → Diamond */
    DIAMOND: GmccDiamondEvent,
    /** App Setting */
    APP_SETTING: MgmtAppSettingEvent,
    /** Lenders */
    LENDERS: MgmtLendersEvent,
    /** Call Reports */
    CALL_REPORTS: MgmtCallReportsEvent,
    /** Company Profile */
    COMPANY_PROFILE: MgmtCompanyProfileEvent,
    /** Public Site Integration */
    PUBLIC_SITE_INTEGRATION: MgmtPublicSiteIntegrationEvent,
    /** Communication Setup */
    COMMUNICATION_SETUP: MgmtCommunicationSetupEvent,
    /** Team Credit Report */
    TEAM_CREDIT_REPORT: MgmtTeamCreditReportEvent,
    /** User Management */
    USER_MANAGEMENT: MgmtUserManagementEvent,
    /** Closing Cost Setting */
    CLOSING_COST_SETTING: MgmtClosingCostSettingEvent,
    /** Billing Management */
    BILLING_MANAGEMENT: MgmtBillingManagementEvent,
    /** 选套餐 / 付款（注册 + Management Plans And Pricing） */
    PAYMENT: PaymentEvent,
    /** Profile */
    PROFILE: SettingsProfileEvent,
    /** Feature Customization */
    FEATURE_CUSTOM: SettingsFeatureCustomizationEvent,
    /** Document Templates */
    DOCUMENT_TEMPLATES: SettingsDocumentTemplatesEvent,
    /** Strata AI */
    STRATA_AI: SettingsStrataAIEvent,
    /** Credit Report */
    CREDIT_REPORT: SettingsPersonalCreditReportEvent,
    /** One Page App Setup */
    ONE_PAGE_APP_SETUP: SettingsOnePageAppSetupEvent,
    /** Open House Sign-in */
    OPEN_HOUSE_SIGN_IN: SettingsOpenHouseSignInEvent,
    /** Growth Hub 首页 */
    GROWTH_HUB: GrowthHubOverviewEvent,
    /** Personal site & Bluerate */
    PERSONAL_SITE: GrowthHubPersonalSiteEvent,
    /** Open house finder */
    OPEN_HOUSE_FINDER: GrowthHubOpenHouseFinderEvent,
    /** 全站公共事件 */
    COMMON: CommonEvent,
}

export type StatsigEventName =
    | AuthEvent
    | DashboardEvent
    | LoansEvent
    | LoanEvent
    | BorrowersEvent
    | BorrowerEvent
    | RatesEvent
    | GmccProgramMapsEvent
    | GmccFlyersHubEvent
    | GmccCelebrityEvent
    | GmccUniverseEvent
    | GmccDiamondEvent
    | MgmtAppSettingEvent
    | MgmtLendersEvent
    | MgmtCallReportsEvent
    | MgmtCompanyProfileEvent
    | MgmtPublicSiteIntegrationEvent
    | MgmtCommunicationSetupEvent
    | MgmtTeamCreditReportEvent
    | MgmtUserManagementEvent
    | MgmtClosingCostSettingEvent
    | MgmtBillingManagementEvent
    | PaymentEvent
    | SettingsProfileEvent
    | SettingsFeatureCustomizationEvent
    | SettingsDocumentTemplatesEvent
    | SettingsStrataAIEvent
    | SettingsPersonalCreditReportEvent
    | SettingsOnePageAppSetupEvent
    | SettingsOpenHouseSignInEvent
    | GrowthHubOverviewEvent
    | GrowthHubPersonalSiteEvent
    | GrowthHubOpenHouseFinderEvent
    | CommonEvent
